/**
 * Export dialog: pick what to build, for which languages and networks, wait,
 * get a link.
 *
 * Scope is either the snapshot in the preview or everything in one folder — the
 * two things someone actually asks for. Language matters here in a way it does
 * not in the preview: the container builds with `--env exportPlayable`, which
 * trims the bundled assets down to the language being built, so one build per
 * language is the point rather than an inconvenience.
 *
 * The build runs in the agent container and takes a couple of minutes per cell
 * of the matrix. It is not waited for here: the editor holds the job (see
 * useSnapshotJobs), so this dialog can be closed the moment it is started and
 * the file is waiting when it is opened again.
 */

import React, { useEffect, useMemo, useState } from "react";
import _ from "lodash";
import BuildWaitLine from "../buildWaitLine";
import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  ListItemText,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Switch,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { collectFolderPaths, normalizeFolderPath } from "../../modules/snapshotTree";
import { describeJobFailureWithReasons, exportSnapshots } from "../../modules/snapshotEditorApi";
import {
  loadingScreenViews,
  networkCaption,
  networkViews,
} from "../../modules/playEngineExportTargets";
import { bestPlayableBytes, formatBytes } from "../../modules/playableSizeEstimate";
import { FIELD_LABEL, PANEL_BG } from "./panelPalette";

// Past this the dialog says out loud how long it is about to take; the server
// refuses outright at 64.
const BUILD_WARNING_THRESHOLD = 8;

const DEFAULT_LOADING_SCREEN_BY_FLOW = { playableGenerator: "playinLoading" };
const DEFAULT_LOADING_SCREEN = "blankLoading";

function defaultLoadingScreenFor(flow) {
  return DEFAULT_LOADING_SCREEN_BY_FLOW[flow] || DEFAULT_LOADING_SCREEN;
}

const useStyles = makeStyles({
  hint: { fontSize: "12px", opacity: 0.7, marginTop: "8px" },
  sizeHint: {
    fontSize: "14px",
    fontWeight: 600,
    color: "#e2e5ea",
    marginTop: "10px",
  },
  warning: { fontSize: "12px", color: "#e0b96a", marginTop: "4px" },
  result: { marginTop: "12px", fontSize: "13px", wordBreak: "break-all" },
  engine: { marginTop: "4px", fontSize: "11px", opacity: 0.6 },
  failure: { marginTop: "8px", fontSize: "12px", color: "#ef9a9a" },
  fonts: { marginTop: "8px", fontSize: "11px", color: FIELD_LABEL, opacity: 0.85, lineHeight: 1.6 },
  fontsTotal: { color: "#a5d6a7" },
  busy: { display: "flex", alignItems: "center", gap: "10px", marginTop: "12px" },
  // Engine fetch, dependency install, place in the build queue — BuildWaitLine.
  waitLine: { display: "block", fontSize: "12px", color: FIELD_LABEL, marginTop: "2px" },
  field: { marginTop: "12px" },
  sectionLabel: { fontSize: "12px", color: FIELD_LABEL, marginTop: "12px" },
  networkOption: { display: "flex", alignItems: "center", gap: "8px" },
  networkLogo: { width: "18px", height: "18px", objectFit: "contain" },
});

export default function DialogExport({ editor, open, onClose }) {
  const classes = useStyles();
  const [scope, setScope] = useState("current");
  const [folderPath, setFolderPath] = useState("");
  const [networks, setNetworks] = useState(["applovin"]);
  const [languages, setLanguages] = useState(["en"]);
  const [allLanguages, setAllLanguages] = useState(false);
  const [loadingScreen, setLoadingScreen] = useState(defaultLoadingScreenFor(editor.flow));
  const [subsetFonts, setSubsetFonts] = useState(true);
  const [result, setResult] = useState(null);

  // The export, held by the editor rather than by this dialog: it runs for
  // minutes in the agent container, and nobody should have to sit in front of
  // it. One at a time — this dialog has one set of options and one result —
  // which is what the empty scope says. See useSnapshotJobs.
  const exportJob = editor.jobs.get("exportSnapshots");
  const isBuilding = _.get(exportJob, "state") === "running";
  const progress = editor.jobs.progressOf(exportJob);
  const connectionDown = _.get(exportJob, "connectionDown") || null;

  const folders = useMemo(() => collectFolderPaths(editor.snapshots), [editor.snapshots]);

  const folderFiles = useMemo(
    () =>
      (editor.snapshots || [])
        .filter((item) => item.isReadable !== false)
        .filter((item) => {
          const itemFolder = normalizeFolderPath(item.exportFolder);
          return itemFolder === folderPath || itemFolder.startsWith(`${folderPath}/`);
        })
        .map((item) => item.fileName),
    [editor.snapshots, folderPath]
  );

  const fileNames = scope === "current"
    ? (editor.selectedFileName ? [editor.selectedFileName] : [])
    : folderFiles;

  /**
   * Every language the selected snapshots declare. The summaries carry it (see
   * the server's listSnapshots), so a folder of forty files does not have to be
   * opened to fill this list.
   */
  const availableLanguages = useMemo(() => {
    const selected = new Set(fileNames);
    const found = new Set();
    for (const item of editor.snapshots || []) {
      if (!selected.has(item.fileName)) continue;
      for (const language of item.languages || []) found.add(language);
    }
    if (found.size === 0) found.add("en");
    return Array.from(found).sort();
  }, [editor.snapshots, fileNames]);

  // A language that is no longer on offer must not stay selected behind the
  // user's back — it would build an empty bundle.
  const effectiveLanguages = useMemo(() => {
    const kept = languages.filter((language) => availableLanguages.includes(language));
    return kept.length ? kept : [availableLanguages[0]];
  }, [availableLanguages, languages]);

  const buildCount =
    fileNames.length * networks.length * (allLanguages ? 1 : effectiveLanguages.length);

  const estimate = useMemo(() => {
    const summaryByName = new Map(
      (editor.snapshots || []).map((item) => [item.fileName, item])
    );

    // THE SAME RULE THE PREVIEW TOOLBAR USES. This used to reach straight for
    // the rule of thumb, so the snapshot open in the editor read one size
    // above the preview and a larger one here.
    const languagesInBuild = allLanguages ? ["all"] : effectiveLanguages;
    let totalBytes = 0;
    let known = 0;

    for (const fileName of fileNames) {
      const isLoaded = fileName === editor.loadedFileName;
      const summary = summaryByName.get(fileName);
      const snapshotBytes =
        isLoaded && editor.snapshotSizeBytes
          ? editor.snapshotSizeBytes
          : summary && summary.sizeBytes;

      let fileBytes = 0;
      for (const network of networks) {
        for (const language of languagesInBuild) {
          const bytes = bestPlayableBytes({
            summary,
            network,
            language,
            serverEstimate: isLoaded ? editor.snapshotEstimatedBytes : null,
            snapshotBytes,
          });
          if (bytes) fileBytes += bytes;
        }
      }

      if (!fileBytes) continue;
      totalBytes += fileBytes;
      known += 1;
    }

    return { bytes: totalBytes, partial: known < fileNames.length };
  }, [
    allLanguages,
    editor.loadedFileName,
    editor.snapshotSizeBytes,
    editor.snapshots,
    effectiveLanguages,
    fileNames,
    networks,
  ]);

  /**
   * What the font shrinking did, per font. The container reports one entry per
   * (snapshot, language, asset); the same font in several languages is listed
   * once with its worst case, which is the number that matters for the bundle.
   */
  const fontSummary = useMemo(() => {
    const entries = _.get(result, "fontReport", []);
    const byFont = new Map();
    let savedBytes = 0;
    let shrunk = 0;

    for (const entry of entries) {
      const label = entry.face || entry.assetKey;
      const key = `${entry.snapshot}:${entry.assetKey}`;
      const saved = (entry.bytesBefore || 0) - (entry.bytesAfter || 0);

      if (entry.skipped) {
        if (!byFont.has(key)) byFont.set(key, { key, text: `${label}: kept whole — ${entry.skipped}` });
        continue;
      }

      const previous = byFont.get(key);
      if (!previous || previous.saved == null || saved < previous.saved) {
        byFont.set(key, {
          key,
          saved,
          text:
            `${label}: ${entry.charsBefore} → ${entry.charsAfter} chars, ` +
            `${formatBytes(entry.bytesBefore)} → ${formatBytes(entry.bytesAfter)}`,
        });
      }
    }

    for (const line of byFont.values()) {
      if (line.saved > 0) {
        savedBytes += line.saved;
        shrunk += 1;
      }
    }

    return { lines: [...byFont.values()], savedBytes, shrunk };
  }, [result]);

  /**
   * Start the export and stop waiting for it.
   *
   * The job belongs to the editor from here on: it keeps polling with this
   * dialog closed, announces its own outcome, and leaves the result for
   * whenever the dialog is opened again — see the effect below.
   */
  function runExport() {
    if (fileNames.length === 0 || networks.length === 0) return;

    setResult(null);

    const folderName = scope === "folder" ? folderPath.split("/").pop() || "export" : "export";
    const options = {
      networks,
      languages: allLanguages ? ["all"] : effectiveLanguages,
      loadingScreen,
      subsetFonts,
    };
    editor.jobs.start({
      // The row this export will mark in the tree — the snapshot or the folder
      // it was asked for. The toolbar button is the other place it shows, and
      // the preview pane it sits in can be collapsed out of sight.
      meta: { target: scope === "folder" ? folderPath : editor.selectedFileName },
      type: "exportSnapshots",
      run: async (hooks) => {
        // Inside the job, so starting one claims the export before the flush
        // has a chance to let a second click through.
        //
        // Whatever is queued must reach the file before it is built, or the
        // export would ship the version on disk rather than the one on screen.
        await editor.flushSave();
        return exportSnapshots(editor.baseID, fileNames, folderName, { ...options, ...hooks });
      },
    });
  }

  useEffect(() => {
    setLoadingScreen(defaultLoadingScreenFor(editor.flow));
  }, [editor.flow]);

  // The export's result, taken up whenever this dialog is on screen — the
  // moment it lands if it is open, and otherwise the next time it is opened.
  useEffect(() => {
    if (!open || !exportJob || exportJob.state !== "finished") return;

    const { response } = exportJob;
    editor.jobs.consume(exportJob.key);

    if (!response.ok) {
      // An export can be run again without consequence — it overwrites its own
      // artifact and nothing leaves the company — so the advice for a lost
      // connection here is the opposite of the delivery dialog's.
      setResult({
        error: describeJobFailureWithReasons(response, {
          fallback: "Export failed.",
          repeatable: true,
        }),
        failed: _.get(response, "error.failed", []),
      });
      return;
    }

    setResult(response.data);

    // Same as the version editor's export and the batch export: the file the
    // user asked for starts downloading by itself. The object is uploaded with
    // `Content-Disposition: attachment`, so this downloads rather than
    // navigating away — and the link below stays for a second copy. It waits
    // for the dialog, because a download nobody is looking at is a surprise.
    if (response.data.file) window.location = response.data.file;
  }, [open, exportJob]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ style: { backgroundColor: PANEL_BG } }}
    >
      <DialogTitle>Export playable</DialogTitle>
      <DialogContent>
        <RadioGroup value={scope} onChange={(event) => setScope(event.target.value)}>
          <FormControlLabel
            value="current"
            control={<Radio size="small" />}
            disabled={isBuilding}
            label={editor.selectedFileName || "No snapshot selected"}
          />
          <FormControlLabel
            value="folder"
            control={<Radio size="small" />}
            disabled={isBuilding || folders.length === 0}
            label="A whole folder"
          />
        </RadioGroup>

        {scope === "folder" && (
          <FormControl fullWidth size="small">
            <InputLabel>Folder</InputLabel>
            <Select
              value={folderPath}
              disabled={isBuilding}
              onChange={(event) => setFolderPath(event.target.value)}
            >
              <MenuItem value="">
                <em>Root</em>
              </MenuItem>
              {folders.map((item) => (
                <MenuItem key={item} value={item}>
                  {item}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <FormControl fullWidth size="small" className={classes.field}>
          <InputLabel>Networks</InputLabel>
          <Select
            multiple
            value={networks}
            disabled={isBuilding}
            onChange={(event) => setNetworks(event.target.value)}
            renderValue={(value) => value.map(networkCaption).join(", ")}
          >
            {networkViews.map((network) => (
              <MenuItem key={network.name} value={network.name}>
                <Checkbox size="small" checked={networks.indexOf(network.name) > -1} />
                <span className={classes.networkOption}>
                  <img src={network.logo} alt="" className={classes.networkLogo} />
                  <ListItemText primary={network.caption} />
                </span>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth size="small" className={classes.field}>
          <InputLabel>Languages</InputLabel>
          <Select
            multiple
            value={effectiveLanguages}
            disabled={isBuilding || allLanguages}
            onChange={(event) => setLanguages(event.target.value)}
            renderValue={(value) => value.join(", ")}
          >
            {availableLanguages.map((language) => (
              <MenuItem key={language} value={language}>
                <Checkbox size="small" checked={effectiveLanguages.indexOf(language) > -1} />
                <ListItemText primary={language} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {availableLanguages.length > 1 && (
          <FormControlLabel
            control={
              <Switch
                size="small"
                color="primary"
                checked={allLanguages}
                disabled={isBuilding}
                onChange={(event) => setAllLanguages(event.target.checked)}
              />
            }
            label={
              <span style={{ fontSize: "13px" }}>
                One build for all languages (device language at runtime)
              </span>
            }
          />
        )}

        <FormControl fullWidth size="small" className={classes.field}>
          <InputLabel>Loading screen</InputLabel>
          <Select
            value={loadingScreen}
            disabled={isBuilding}
            onChange={(event) => setLoadingScreen(event.target.value)}
          >
            {loadingScreenViews.map((screen) => (
              <MenuItem key={screen.id} value={screen.id}>
                {screen.caption}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControlLabel
          control={
            <Switch
              size="small"
              color="primary"
              checked={subsetFonts}
              disabled={isBuilding}
              onChange={(event) => setSubsetFonts(event.target.checked)}
            />
          }
          label={
            <span style={{ fontSize: "13px" }}>
              Shrink fonts to used characters
            </span>
          }
        />

        <Typography className={classes.hint}>
          {fileNames.length === 0 || networks.length === 0
            ? "Nothing to export."
            : `${buildCount} build(s). Each is named after its metadata.exportName, or its file name when that is not set.`}
        </Typography>

        {estimate.bytes > 0 && (
          <Typography className={classes.sizeHint}>
            {buildCount > 1
              ? `Estimated size ~${formatBytes(estimate.bytes)} across all builds`
              : `Estimated size ~${formatBytes(estimate.bytes)}`}
            {estimate.partial ? ", plus the files whose size is not known yet." : "."}
          </Typography>
        )}

        {buildCount > BUILD_WARNING_THRESHOLD && (
          <Typography className={classes.warning}>
            That is a lot of builds — they run one at a time, so expect several minutes each.
          </Typography>
        )}

        {isBuilding && (
          <div className={classes.busy}>
            <CircularProgress size={18} />
            <div>
              <Typography style={{ fontSize: "13px" }}>
                {progress || "Building in the agent container — a couple of minutes per playable."}
              </Typography>
              <BuildWaitLine events={_.get(exportJob, "events")} active={isBuilding} className={classes.waitLine} />
            </div>
          </div>
        )}

        {/* The progress line stops moving when the connection goes; on its own
            that reads as a hang, and the build is not even involved. */}
        {isBuilding && connectionDown && (
          <Typography className={classes.warning}>
            Lost contact with the server {Math.round((connectionDown.sinceMs || 0) / 1000)}s ago —
            still retrying. The build keeps running there, whether or not this stays open.
          </Typography>
        )}

        {result && result.file && (
          <>
            <Typography className={classes.result}>
              Downloading{" "}
              <a href={result.file} target="_blank" rel="noreferrer">
                {result.fileName}
              </a>
              {" "}— click if it did not start.
            </Typography>
            {result.engine && (
              <Typography className={classes.engine}>
                Built from playEngine {result.engine.branch}
                {result.engine.commit ? ` @ ${result.engine.commit}` : ""}
                {result.engine.engineVersion ? ` (engine ${result.engine.engineVersion})` : ""}
              </Typography>
            )}
          </>
        )}

        {result && (result.fontReport || []).length > 0 && (
          <div className={classes.fonts}>
            {fontSummary.savedBytes > 0 && (
              <div className={classes.fontsTotal}>
                Fonts: {formatBytes(fontSummary.savedBytes)} saved across{" "}
                {fontSummary.shrunk} font{fontSummary.shrunk === 1 ? "" : "s"}
              </div>
            )}
            {fontSummary.lines.map((line) => (
              <div key={line.key}>{line.text}</div>
            ))}
          </div>
        )}

        {result && (result.warnings || []).length > 0 && (
          <div className={classes.warning}>
            {result.warnings.map((message) => (
              <div key={message}>{message}</div>
            ))}
          </div>
        )}

        {result && (result.failed || []).length > 0 && (
          <div className={classes.failure}>
            {result.failed.map((item, index) => (
              <div key={`${item.snapshot}-${item.network || ""}-${item.language || ""}-${index}`}>
                {item.snapshot}
                {item.network ? ` (${item.network}/${item.language})` : ""}: {item.error}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          Close
        </Button>
        <Button
          color="primary"
          variant="contained"
          disabled={isBuilding || fileNames.length === 0 || networks.length === 0}
          onClick={runExport}
        >
          Export
        </Button>
      </DialogActions>
    </Dialog>
  );
}
